// Handle post-login actions on the library surface

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { consumeAuthCompletionPending } from '../utilities/authCompletion';
import { consumePurchaseIntent } from '../utilities/purchaseIntent';
import type { PurchaseIntent } from '../utilities/purchaseIntent';
import { analyticsService } from '../services/analyticsService';
import { refreshTwitchModListsInBackground } from '../services/twitchModListRefresh';
import { createCheckoutSession } from '../transport/api/protectedApi';
import type { AnalyticsSurface } from '../types/analytics';
import type { UserAuthFields } from '@airhead-games/shared/types';

type UseAuthPostLoginParams = {
  user: UserAuthFields | null | undefined;
  isAuthInitialized: boolean;
  analyticsSurface: AnalyticsSurface;
  showWelcome: () => void;
  closeDialog: () => void;
  setUser: (user: UserAuthFields | null) => void;
};

/**
 * Runs exactly once when the user transitions from signed-out to signed-in
 * on the library surface, regardless of whether they arrived via email OTP
 * (dialog completes in place) or Twitch OAuth (page reloads after redirect).
 *
 * Decides between three outcomes:
 *   1. Resume a deferred Stripe Checkout (if a purchase intent is stored).
 *   2. Show the first-sign-in welcome step (if `signInCount <= 1`).
 *   3. Toast "Signed in successfully" and close the dialog.
 *
 * Also fires the `sign_up` / `sign_in` analytics events, so every sign-in
 * path reports them, not only email OTP.
 */
export function useAuthPostLogin({
  user,
  isAuthInitialized,
  analyticsSurface,
  showWelcome,
  closeDialog,
  setUser,
}: UseAuthPostLoginParams): void {
  const handledRef = useRef(false);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!user) {
      handledRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthInitialized) {
      return;
    }

    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = user?._id ?? null;

    if (!user || handledRef.current) {
      return;
    }

    const hasAuthCompletionMarker = consumeAuthCompletionPending();
    const transitionedFromSignedOut = previousUserId === null;

    if (!hasAuthCompletionMarker && !transitionedFromSignedOut) {
      return;
    }

    handledRef.current = true;

    // Login-only Twitch mod-list refresh (toggle sync stays in library hooks).
    // Non-blocking: Twitch failures must not affect sign-in UX.
    void refreshTwitchModListsInBackground(user).then((refreshedUser) => {
      if (refreshedUser) {
        setUser(refreshedUser);
      }
    });

    const isFirstSignIn = (user.signInCount ?? 0) <= 1;

    if (isFirstSignIn) {
      analyticsService.trackSignUp({
        sign_in_count: user.signInCount ?? 1,
        analytics_audience: 'user',
      });
    } else {
      analyticsService.trackSignIn({
        sign_in_count: user.signInCount,
        analytics_audience: 'user',
      });
    }

    const completeWithoutCheckout = () => {
      if (isFirstSignIn) {
        showWelcome();
        return;
      }
      toast.success('Signed in successfully');
      closeDialog();
    };

    const intent = consumePurchaseIntent();
    if (!intent) {
      completeWithoutCheckout();
      return;
    }

    resumeStripeCheckout({
      intent,
      isFirstSignIn,
      analyticsSurface,
      onRedirect: (url) => {
        closeDialog();
        window.location.href = url;
      },
      onFailure: completeWithoutCheckout,
    });
  }, [
    user,
    isAuthInitialized,
    analyticsSurface,
    showWelcome,
    closeDialog,
    setUser,
  ]);
}

type ResumeCheckoutParams = {
  intent: PurchaseIntent;
  isFirstSignIn: boolean;
  analyticsSurface: AnalyticsSurface;
  onRedirect: (url: string) => void;
  onFailure: () => void;
};

function resumeStripeCheckout({
  intent,
  isFirstSignIn,
  analyticsSurface,
  onRedirect,
  onFailure,
}: ResumeCheckoutParams): void {
  const { stripePriceId, checkoutAttemptId } = intent;
  const cancelPath = isFirstSignIn ? '/library' : undefined;
  const baseAnalytics = {
    surface: analyticsSurface,
    flow_type: 'stripe_checkout' as const,
    checkout_attempt_id: checkoutAttemptId,
    stripe_price_id: stripePriceId,
  };

  const checkoutPromise = createCheckoutSession(stripePriceId, cancelPath).then(
    (response) => {
      if (!response.success || !response.data?.url) {
        const errorCode = response.success
          ? 'checkout_session_missing_url'
          : 'checkout_session_request_failed';
        const errorMessage = response.success
          ? 'Checkout session missing URL'
          : 'Checkout session request failed';
        analyticsService.trackCheckoutFailed({
          ...baseAnalytics,
          checkout_result: 'error',
          error_code: errorCode,
          error_message: errorMessage,
        });
        throw new Error(errorMessage);
      }
      analyticsService.trackCheckoutRedirected({
        ...baseAnalytics,
        checkout_result: 'redirected',
        navigation_target: response.data.url,
        cancel_path: cancelPath,
      });
      return response.data.url;
    }
  );

  toast.promise(checkoutPromise, {
    loading: 'Redirecting to Stripe…',
    success: (url) => {
      onRedirect(url);
      return 'Redirecting to Stripe…';
    },
    error: (err) => {
      // `checkoutPromise` already fired `trackCheckoutFailed` for known
      // shapes; catch-all here covers the exception path.
      if (!(err instanceof Error) || !isKnownCheckoutFailure(err.message)) {
        analyticsService.trackCheckoutFailed({
          ...baseAnalytics,
          checkout_result: 'error',
          error_code: 'checkout_session_exception',
          error_message: err instanceof Error ? err.message : String(err),
        });
      }
      onFailure();
      return 'Could not open checkout. Please try again.';
    },
  });
}

const KNOWN_CHECKOUT_FAILURE_MESSAGES = new Set([
  'Checkout session missing URL',
  'Checkout session request failed',
]);

const isKnownCheckoutFailure = (message: string): boolean =>
  KNOWN_CHECKOUT_FAILURE_MESSAGES.has(message);
